/**
 * ActiveFilters Component
 * Display and manage active filter chips
 */

import React from 'react';
import { X } from 'lucide-react';
import type { OKRFilters, FilterMetadata } from '../../types/okr';
import { okrStatusLabel } from '../../utils/okrStatus';

interface ActiveFiltersProps {
  filters: OKRFilters;
  metadata: FilterMetadata | null;
  onRemoveFilter: (filterKey: keyof OKRFilters, value?: string) => void;
  onClearAll: () => void;
}

export const ActiveFilters: React.FC<ActiveFiltersProps> = ({
  filters,
  metadata,
  onRemoveFilter,
  onClearAll,
}) => {
  const activeFilters: Array<{
    key: keyof OKRFilters;
    label: string;
    value: string;
    displayValue: string;
  }> = [];

  // Search filter
  if (filters.search) {
    activeFilters.push({
      key: 'search',
      label: 'Search',
      value: filters.search,
      displayValue: `"${filters.search}"`,
    });
  }

  // Type filters
  if (filters.types && filters.types.length > 0) {
    filters.types.forEach((type) => {
      activeFilters.push({
        key: 'types',
        label: 'Type',
        value: type,
        displayValue: type.charAt(0).toUpperCase() + type.slice(1),
      });
    });
  }

  // Status filters
  if (filters.statuses && filters.statuses.length > 0) {
    filters.statuses.forEach((status) => {
      activeFilters.push({
        key: 'statuses',
        label: 'Status',
        value: status,
        displayValue: okrStatusLabel(status),
      });
    });
  }

  // Owner filters
  if (filters.ownerIds && filters.ownerIds.length > 0 && metadata) {
    filters.ownerIds.forEach((ownerId) => {
      const owner = metadata.owners.find((o) => o.id === ownerId);
      if (owner) {
        activeFilters.push({
          key: 'ownerIds',
          label: 'Owner',
          value: ownerId,
          displayValue: owner.name,
        });
      }
    });
  }

  // Department filters — `filters.departmentIds` holds `Department.id` uuids, so the chip
  // looks the name up in metadata the same way the owner and cycle chips do. An id metadata
  // does not know (stale link, deleted department) renders no chip rather than the raw uuid.
  if (filters.departmentIds && filters.departmentIds.length > 0 && metadata) {
    filters.departmentIds.forEach((departmentId) => {
      const department = metadata.departments.find((d) => d.id === departmentId);
      if (department) {
        activeFilters.push({
          key: 'departmentIds',
          label: 'Department',
          value: departmentId,
          displayValue: department.name,
        });
      }
    });
  }

  // Cycle filters
  if (filters.cycleIds && filters.cycleIds.length > 0 && metadata) {
    filters.cycleIds.forEach((cycleId) => {
      const cycle = metadata.cycles?.find((c) => c.id === cycleId);
      if (cycle) {
        activeFilters.push({
          key: 'cycleIds',
          label: 'Cycle',
          value: cycleId,
          displayValue: cycle.name,
        });
      }
    });
  }

  // Progress filter
  if (filters.progressMin !== undefined || filters.progressMax !== undefined) {
    const min = filters.progressMin ?? 0;
    const max = filters.progressMax ?? 100;
    if (min > 0 || max < 100) {
      activeFilters.push({
        key: 'progressMin',
        label: 'Progress',
        value: `${min}-${max}`,
        displayValue: `${min}% - ${max}%`,
      });
    }
  }

  // Date filter
  if (filters.startDate || filters.endDate) {
    const start = filters.startDate ? new Date(filters.startDate).toLocaleDateString() : 'Any';
    const end = filters.endDate ? new Date(filters.endDate).toLocaleDateString() : 'Any';
    activeFilters.push({
      key: 'startDate',
      label: 'Date Range',
      value: `${filters.startDate}-${filters.endDate}`,
      displayValue: `${start} - ${end}`,
    });
  }

  // Filter mode (only show if OR, since AND is default)
  const filterMode = filters.filterMode || 'AND';

  if (activeFilters.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm font-medium text-gray-700">Active Filters:</span>

      {filterMode === 'OR' && activeFilters.length > 1 && (
        <div className="px-3 py-1.5 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
          <span className="text-purple-600">Mode:</span> Match ANY
        </div>
      )}

      {activeFilters.map((filter, index) => (
        <div
          key={`${filter.key}-${filter.value}-${index}`}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-100 text-blue-800 rounded-full text-sm font-medium"
        >
          <span className="text-blue-600">{filter.label}:</span>
          <span>{filter.displayValue}</span>
          <button
            onClick={() => {
              if (filter.key === 'search') {
                onRemoveFilter('search');
              } else if (filter.key === 'progressMin') {
                onRemoveFilter('progressMin');
                onRemoveFilter('progressMax');
              } else if (filter.key === 'startDate') {
                onRemoveFilter('startDate');
                onRemoveFilter('endDate');
              } else if (
                filter.key === 'types' ||
                filter.key === 'statuses' ||
                filter.key === 'ownerIds' ||
                filter.key === 'departmentIds' ||
                filter.key === 'cycleIds'
              ) {
                onRemoveFilter(filter.key, filter.value);
              }
            }}
            className="ml-1 hover:bg-blue-200 rounded-full p-0.5 transition-colors"
            title="Remove filter"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      ))}

      {activeFilters.length > 1 && (
        <button
          onClick={onClearAll}
          className="px-3 py-1.5 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-full transition-colors"
        >
          Clear All
        </button>
      )}
    </div>
  );
};
